import React from 'react';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { Box, Skeleton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';

function getPresetFilterItems(filterPreset) {
  if (filterPreset === 'bevOnly') {
    return [{ id: 'preset-bev', field: 'vehicleType', operator: 'contains', value: 'BEV' }];
  }

  if (filterPreset === 'year2020') {
    return [{ id: 'preset-year', field: 'modelYear', operator: '>=', value: '2020' }];
  }

  if (filterPreset === 'range200') {
    return [{ id: 'preset-range', field: 'batteryRange', operator: '>=', value: '200' }];
  }

  return [];
}

// Skeleton row component
const SkeletonRow = () => (
  <Box sx={{ display: 'flex', gap: 1, p: 1 }}>
    {/* Checkbox column */}
    <Skeleton variant="rectangular" width={28} height={32} />
    {/* Data columns */}
    <Skeleton variant="rectangular" width={150} height={32} sx={{ flexGrow: 1 }} />
    <Skeleton variant="rectangular" width={150} height={32} sx={{ flexGrow: 1 }} />
    <Skeleton variant="rectangular" width={100} height={32} />
    <Skeleton variant="rectangular" width={150} height={32} sx={{ flexGrow: 1 }} />
    <Skeleton variant="rectangular" width={150} height={32} sx={{ flexGrow: 1 }} />
    <Skeleton variant="rectangular" width={150} height={32} sx={{ flexGrow: 1 }} />
  </Box>
);

const columns = [
  {
    field: 'make',
    headerName: 'Manufacturer',
    flex: 1,
    sortable: true,
    filterable: true
  },
  {
    field: 'model',
    headerName: 'Model',
    flex: 1,
    sortable: true,
    filterable: true
  },
  {
    field: 'modelYear',
    headerName: 'Year',
    width: 100,
    type: 'number',
    valueGetter: (value) => (Number.isFinite(Number(value)) ? Number(value) : null),
    sortable: true,
    filterable: true
  },
  {
    field: 'batteryRange',
    headerName: 'Battery Range (mi)',
    width: 150,
    type: 'number',
    valueGetter: (value) => (Number.isFinite(Number(value)) ? Number(value) : null),
    sortable: true,
    filterable: true,
    valueFormatter: (value) => {
      if (value === null || value === undefined) {
        return 'N/A';
      }
      const numValue = Number(value);
      if (isNaN(numValue) || numValue <= 0) {
        return 'N/A';
      }
      return `${Math.round(numValue)} mi`;
    },
    sortComparator: (v1, v2) => {
      if (v1 === null || v1 === undefined) return -1;
      if (v2 === null || v2 === undefined) return 1;
      return Number(v1) - Number(v2);
    }
  },
  {
    field: 'location',
    headerName: 'Location',
    flex: 1,
    sortable: true,
    filterable: true
  },
  {
    field: 'vehicleType',
    headerName: 'Vehicle Type',
    flex: 1,
    sortable: true,
    filterable: true
  }
];

export default function CustomizedDataGrid({ loading, chartData, searchQuery, filterPreset }) {
  const theme = useTheme();
  const tableData = chartData || [];
  const [manualFilterItems, setManualFilterItems] = React.useState([]);
  const quickFilterValues = React.useMemo(
    () => (searchQuery?.trim() ? [searchQuery.trim()] : []),
    [searchQuery],
  );
  const presetFilterItems = React.useMemo(() => getPresetFilterItems(filterPreset), [filterPreset]);

  if (loading) {
    return (
      <Box sx={{
        width: '100%',
        border: '1px solid rgba(224, 224, 224, 1)',
        borderRadius: 1,
        bgcolor: 'background.paper'
      }}>
        {/* Toolbar placeholder */}
        <Box sx={{ p: 2, borderBottom: '1px solid rgba(224, 224, 224, 1)' }}>
          <Skeleton variant="rectangular" height={40} sx={{ bgcolor: theme.palette.background.paper }} />
        </Box>

        {/* Skeleton rows */}
        {[...Array(7)].map((_, index) => (
          <SkeletonRow key={index} sx={{ bgcolor: theme.palette.background.paper }} />
        ))}
      </Box>
    );
  }

  return (
    <DataGrid
      autoHeight
      checkboxSelection
      rows={tableData}
      columns={columns}
      getRowClassName={(params) =>
        params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
      }
      initialState={{
        pagination: {
          paginationModel: { pageSize: 20, page: 0 },
        },
        sorting: {
          sortModel: [{ field: 'make', sort: 'asc' }],
        },
      }}
      filterModel={{ items: [...presetFilterItems, ...manualFilterItems], quickFilterValues }}
      onFilterModelChange={(newModel) => {
        const userItems = (newModel?.items || []).filter((item) => {
          const id = String(item?.id || '');
          return !id.startsWith('preset-');
        });
        setManualFilterItems(userItems);
      }}
      pageSizeOptions={[10, 20, 50]}
      loading={loading}
      getRowId={(row) => row.id}

      disableRowSelectionOnClick
      slots={{
        toolbar: GridToolbar,
      }}
      slotProps={{
        toolbar: {
          showQuickFilter: true,
          quickFilterProps: { debounceMs: 500 },
        },
        filterPanel: {
          filterFormProps: {
            logicOperatorInputProps: {
              variant: 'outlined',
              size: 'small',
            },
            columnInputProps: {
              variant: 'outlined',
              size: 'small',
              sx: { mt: 'auto' },
            },
            operatorInputProps: {
              variant: 'outlined',
              size: 'small',
              sx: { mt: 'auto' },
            },
            valueInputProps: {
              InputComponentProps: {
                variant: 'outlined',
                size: 'small',
              },
            },
          },
        },
      }}
      sx={{
        '& .MuiDataGrid-cell': {
          fontSize: '0.875rem'
        }
      }}
    />
  );
}

CustomizedDataGrid.propTypes = {
  loading: PropTypes.bool,
  chartData: PropTypes.arrayOf(PropTypes.object),
  searchQuery: PropTypes.string,
  filterPreset: PropTypes.oneOf(['all', 'bevOnly', 'year2020', 'range200']),
};

CustomizedDataGrid.defaultProps = {
  loading: false,
  chartData: [],
  searchQuery: '',
  filterPreset: 'all',
};
